import { useState, useEffect, useCallback } from 'react';
import { PlanService, Plan, CreatePlanDto, UpdatePlanDto } from '../services/plan.service';
import { message } from 'antd';

interface UsePlansReturn {
  plans: Plan[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  createPlan: (dto: CreatePlanDto) => Promise<Plan>;
  updatePlan: (id: string, dto: UpdatePlanDto) => Promise<Plan>;
  activatePlan: (id: string) => Promise<Plan>;
  deactivatePlan: (id: string) => Promise<Plan>;
  deletePlan: (id: string) => Promise<void>;
  getPlanById: (id: string) => Promise<Plan>;
}

export const usePlans = (): UsePlansReturn => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await PlanService.listPlans();
      setPlans(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch plans');
      setError(error);
      message.error('Failed to load plans: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const createPlan = useCallback(
    async (dto: CreatePlanDto): Promise<Plan> => {
      try {
        const newPlan = await PlanService.createPlan(dto);
        setPlans((prev) => [...prev, newPlan]);
        message.success('Plan created successfully');
        return newPlan;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create plan');
        message.error('Failed to create plan: ' + error.message);
        throw error;
      }
    },
    [],
  );

  const updatePlan = useCallback(
    async (id: string, dto: UpdatePlanDto): Promise<Plan> => {
      try {
        const updated = await PlanService.updatePlan(id, dto);
        setPlans((prev) => prev.map((p) => (p._id === id ? updated : p)));
        message.success('Plan updated successfully');
        return updated;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update plan');
        message.error('Failed to update plan: ' + error.message);
        throw error;
      }
    },
    [],
  );

  const activatePlan = useCallback(
    async (id: string): Promise<Plan> => {
      try {
        const updated = await PlanService.activatePlan(id);
        setPlans((prev) => prev.map((p) => (p._id === id ? updated : p)));
        message.success('Plan activated successfully');
        return updated;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to activate plan');
        message.error('Failed to activate plan: ' + error.message);
        throw error;
      }
    },
    [],
  );

  const deactivatePlan = useCallback(
    async (id: string): Promise<Plan> => {
      try {
        const updated = await PlanService.deactivatePlan(id);
        setPlans((prev) => prev.map((p) => (p._id === id ? updated : p)));
        message.success('Plan deactivated successfully');
        return updated;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to deactivate plan');
        message.error('Failed to deactivate plan: ' + error.message);
        throw error;
      }
    },
    [],
  );

  const deletePlan = useCallback(
    async (id: string): Promise<void> => {
      try {
        await PlanService.deletePlan(id);
        setPlans((prev) => prev.filter((p) => p._id !== id));
        message.success('Plan deleted successfully');
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to delete plan');
        message.error('Failed to delete plan: ' + error.message);
        throw error;
      }
    },
    [],
  );

  const getPlanById = useCallback(
    async (id: string): Promise<Plan> => {
      try {
        return await PlanService.getPlanById(id);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch plan');
        message.error('Failed to fetch plan: ' + error.message);
        throw error;
      }
    },
    [],
  );

  return {
    plans,
    loading,
    error,
    refetch: fetchPlans,
    createPlan,
    updatePlan,
    activatePlan,
    deactivatePlan,
    deletePlan,
    getPlanById,
  };
};
